import { useMemo } from "react";
import type { ColumnsType } from "antd/es/table";
import Datatable from "../../../core/common/dataTable";
import ExportExcel from "../../../core/common/exportExcel";
import useApi from "../../../API/useApi";

interface TeacherRecord {
    key: string;
    timestamp: string;
    emailAddress: string;
    email: string;
    kvCode: string;
    kvName: string;
    stateOfKVSchool: string;
    fullName: string;
    educationQualification: string;
    gender: string;
    contactNo: string;
    whatsappNo: string;
    emergencyContactNo: string;
    emailId: string;
    bloodGroup: string;
    arrivalDate: string;
    arrivalTime: string;
    departureDate: string;
    departureTime: string;
    bringingLaptop: string;
    bringingHardwareKit: string;
    bringingPassportPhotos: string;
    mealPreference: string;
}

interface ApiConfirmedUser {
    name: string;
    phone_res: string;
    created_on: string;
    state: string | null;
    parent_email: string | null;
    whatsapp_contact_number: string | null;
    emergency_contact_number: string | null;
    gender: string | null;
    qualification: string | null;
    blood_group: string | null;
    arrival_date: string | null;
    arrival_time: string | null;
    departure_date: string | null;
    departure_time: string | null;
    bringing_laptop: number | null;
    hardware_kit: string | null;
    bring_two_photos: number | null;
    meal_preference: string | null;
}

function formatDate(iso: string | null | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function formatDateTime(iso: string | null | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
        d.getHours()
    )}:${pad(d.getMinutes())}`;
}

function formatBoolFlag(value: number | null | undefined): string {
    if (value === 1) return "Yes";
    if (value === 0) return "No";
    return "";
}

function mapUserToRecord(user: ApiConfirmedUser, index: number): TeacherRecord {
    return {
        key: `${user.phone_res}-${index}`,
        timestamp: formatDateTime(user.created_on),
        emailAddress: user.parent_email ?? "",
        email: "", // no separate field in API response
        kvCode: "", // not present in API response
        kvName: "", // not present in API response
        stateOfKVSchool: user.state ?? "",
        fullName: user.name ?? "",
        educationQualification: user.qualification ?? "",
        gender: user.gender ?? "",
        contactNo: user.phone_res ?? "",
        whatsappNo: user.whatsapp_contact_number ?? "",
        emergencyContactNo: user.emergency_contact_number ?? "",
        emailId: "", // duplicate of emailAddress in original interface; API has only one email field
        bloodGroup: user.blood_group ?? "",
        arrivalDate: formatDate(user.arrival_date),
        arrivalTime: user.arrival_time ?? "",
        departureDate: formatDate(user.departure_date),
        departureTime: user.departure_time ?? "",
        bringingLaptop: formatBoolFlag(user.bringing_laptop),
        bringingHardwareKit: user.hardware_kit ?? "",
        bringingPassportPhotos: formatBoolFlag(user.bring_two_photos),
        mealPreference: user.meal_preference ?? "",
    };
}

export const columns: ColumnsType<TeacherRecord> = [
    {
        title: "Timestamp",
        dataIndex: "timestamp",
        key: "timestamp",
        width: 160,
    },
    {
        title: "Email Address",
        dataIndex: "emailAddress",
        key: "emailAddress",
        width: 200,
        ellipsis: true,
    },
    // {
    //     title: "Email",
    //     dataIndex: "email",
    //     key: "email",
    //     width: 200,
    //     ellipsis: true,
    // },
    // {
    //     title: "KV Code (4 Digit Code)",
    //     dataIndex: "kvCode",
    //     key: "kvCode",
    //     width: 140,
    // },
    // {
    //     title: "Name of KV",
    //     dataIndex: "kvName",
    //     key: "kvName",
    //     width: 180,
    //     ellipsis: true,
    // },
    {
        title: "State of the KV School",
        dataIndex: "stateOfKVSchool",
        key: "stateOfKVSchool",
        width: 160,
        ellipsis: true,
    },
    {
        title: "Full Name as per Service Record",
        dataIndex: "fullName",
        key: "fullName",
        width: 200,
        ellipsis: true,
    },
    {
        title: "Education Qualification with Specialization",
        dataIndex: "educationQualification",
        key: "educationQualification",
        width: 220,
        ellipsis: true,
    },
    {
        title: "Gender",
        dataIndex: "gender",
        key: "gender",
        width: 90,
    },
    {
        title: "Contact No. of Teacher",
        dataIndex: "contactNo",
        key: "contactNo",
        width: 160,
        ellipsis: true,
    },
    {
        title: "Whatsapp No. of Teacher",
        dataIndex: "whatsappNo",
        key: "whatsappNo",
        width: 160,
        ellipsis: true,
    },
    {
        title: "Emergency Contact No.",
        dataIndex: "emergencyContactNo",
        key: "emergencyContactNo",
        width: 160,
        ellipsis: true,
    },
    // {
    //     title: "Email ID",
    //     dataIndex: "emailId",
    //     key: "emailId",
    //     width: 200,
    //     ellipsis: true,
    // },
    {
        title: "Blood Group of the Teacher",
        dataIndex: "bloodGroup",
        key: "bloodGroup",
        width: 140,
        ellipsis: true,
    },
    {
        title: "Date of Arrival at the Venue",
        dataIndex: "arrivalDate",
        key: "arrivalDate",
        width: 160,
        ellipsis: true,
    },
    {
        title: "Arrival Time at the Venue",
        dataIndex: "arrivalTime",
        key: "arrivalTime",
        width: 140,
        ellipsis: true,
    },
    {
        title: "Date of Departure from the Venue",
        dataIndex: "departureDate",
        key: "departureDate",
        width: 160,
        ellipsis: true,
    },
    {
        title: "Departure Time from the Venue",
        dataIndex: "departureTime",
        key: "departureTime",
        width: 140,
        ellipsis: true,
    },
    {
        title: "I am bringing Laptop",
        dataIndex: "bringingLaptop",
        key: "bringingLaptop",
        width: 140,
    },
    {
        title: "I am bringing the Hardware Kit as shared in prerequisites list",
        dataIndex: "bringingHardwareKit",
        key: "bringingHardwareKit",
        width: 200,
        ellipsis: true,
    },
    {
        title: "I am bringing two passport size photos",
        dataIndex: "bringingPassportPhotos",
        key: "bringingPassportPhotos",
        width: 160,
        ellipsis: true,
    },
    {
        title: "Meal Preference",
        dataIndex: "mealPreference",
        key: "mealPreference",
        width: 140,
    },
];

export default function RegisteredUserTable() {
    const { data, loading } = useApi("/get-confirmed-list", {
        method: "GET",
        autoCall: true,
    });

    const tableData = useMemo<TeacherRecord[]>(() => {
        const users: ApiConfirmedUser[] = data?.data ?? [];
        return users.map(mapUserToRecord);
    }, [data]);

    return (
        <div className="page-wrapper">
            <div className="content">

                <div className="card shadow-sm border-0 rounded-4 mb-3">
                    <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <h1 className="mb-0" style={{ fontSize: "1.5rem", fontWeight: 600 }}>
                            Registered User Page
                        </h1>

                        <ExportExcel
                            columns={columns}
                            dataSource={tableData}
                            fileName="registered_users"
                            buttonLabel="Export to Excel"
                        />
                    </div>
                </div>

                <div className="card shadow-sm border-0 rounded-4">
                    <div className="card-body p-0">
                        <Datatable
                            columns={columns}
                            dataSource={tableData}
                            loading={loading}
                        // rowKey="timestamp"
                        // scroll={{ x: 2500 }}
                        />
                    </div>
                </div>

            </div>
        </div>
    )
}